import { UnifiedPrioritySelector } from '@/components/global/selectors/UnifiedPrioritySelector';
import { UnifiedStatusSelector } from '@/components/global/selectors/UnifiedStatusSelector';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/shadcn/avatar';
import { Badge } from '@/components/ui/shadcn/badge';
import { Button } from '@/components/ui/shadcn/button';
import { formatCurrency } from '@/lib/utils';
import type { Lead } from '@/types';
import { Link } from '@inertiajs/react';
import { ColumnDef } from '@tanstack/react-table';
import { ArrowUpDown, Building2, User } from 'lucide-react';

// Translation function type
type TranslateFn = (key: string, fallback?: string) => string;

const matchesFilter = (value: string | null | undefined, filterValue: unknown) => {
    if (!filterValue) {
        return true;
    }

    if (Array.isArray(filterValue)) {
        if (filterValue.length === 0) {
            return true;
        }

        return value ? filterValue.includes(value) : false;
    }

    if (filterValue === 'all') {
        return true;
    }

    return value === filterValue;
};

export const getColumns = (approvalThreshold: number = 10, t: TranslateFn = (k, f) => f || k): ColumnDef<Lead>[] => [
    {
        accessorKey: 'id',
        header: t('leads.tables.id', 'ID'),
    },
    {
        accessorKey: 'lead_number',
        header: ({ column }) => {
            return (
                <Button variant="ghost" onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}>
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                    {t('leads.tables.lead_number', 'Lead Number')}
                </Button>
            );
        },
        cell: ({ row }) => {
            const lead = row.original;
            return (
                <Link
                    href={`/leads/${lead.id}`}
                    className="hover:bg-accent text-foreground w-full rounded px-2 py-1.5 text-left font-semibold transition-colors"
                >
                    {lead.lead_number || lead.id}
                </Link>
            );
        },
    },
    {
        accessorKey: 'estimated_value',
        header: ({ column }) => {
            return (
                <Button variant="ghost" onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}>
                    {t('leads.tables.estimated_value', 'Estimated Value')}
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
            );
        },
        cell: ({ row }) => {
            const lead = row.original;

            // Get budget/estimated value (use approved offer's sales_price if available)
            let budget = lead.estimated_value || lead.value || 0;
            let totalCosts = lead.total_costs || 0;
            let latestApprovedOffer = null;

            if (lead.offers && Array.isArray(lead.offers) && lead.offers.length > 0) {
                // Find the latest approved offer
                const approvedOffers = lead.offers.filter((offer) => offer && offer.status === 'approved');
                if (approvedOffers.length > 0) {
                    // Sort by created_at descending to get the latest
                    latestApprovedOffer = approvedOffers.sort((a, b) => {
                        const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
                        const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
                        return dateB - dateA;
                    })[0];
                    if (latestApprovedOffer && latestApprovedOffer.sales_price) {
                        budget = Number(latestApprovedOffer.sales_price) || budget;
                        // Use approved offer's total_costs if available
                        if (latestApprovedOffer.total_costs !== undefined) {
                            totalCosts = Number(latestApprovedOffer.total_costs) || 0;
                        } else if (latestApprovedOffer.costLines && Array.isArray(latestApprovedOffer.costLines)) {
                            totalCosts = latestApprovedOffer.costLines.reduce(
                                (sum: number, cost: { amount: number }) => sum + Number(cost.amount || 0),
                                0,
                            );
                        }
                    }
                }
            }

            // Calculate profit and percentage
            const budgetValue = Number(budget) || 0;
            const costsValue = Number(totalCosts) || 0;
            const profit = budgetValue - costsValue;
            const profitPercentage = budgetValue > 0 ? (profit / budgetValue) * 100 : 0;

            // Determine color based on profit percentage
            let percentageColor = 'text-muted-foreground';
            if (profitPercentage >= approvalThreshold) {
                percentageColor = 'text-success-subtle-foreground';
            } else if (profitPercentage > 0) {
                percentageColor = 'text-warning-subtle-foreground';
            } else if (profitPercentage < 0) {
                percentageColor = 'text-destructive';
            }

            const formatted = formatCurrency(budgetValue);

            return (
                <div className="flex items-center gap-2 text-left font-medium">
                    <span>{formatted}</span>
                    {budgetValue > 0 && (
                        <span className={`text-xs font-normal ${percentageColor}`}>
                            ({profitPercentage >= 0 ? '+' : ''}
                            {profitPercentage.toFixed(1)}%)
                        </span>
                    )}
                </div>
            );
        },
        sortingFn: (rowA, rowB) => {
            const getEstimatedValue = (lead: Lead) => {
                // Check if there's an approved offer and use its sales_price
                let amount = lead.estimated_value || 0;
                if (lead.offers && Array.isArray(lead.offers) && lead.offers.length > 0) {
                    const approvedOffers = lead.offers.filter((offer) => offer && offer.status === 'approved');
                    if (approvedOffers.length > 0) {
                        const latestApprovedOffer = approvedOffers.sort((a, b) => {
                            const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
                            const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
                            return dateB - dateA;
                        })[0];
                        if (latestApprovedOffer && latestApprovedOffer.sales_price) {
                            amount = Number(latestApprovedOffer.sales_price) || amount;
                        }
                    }
                }
                return amount;
            };

            const a = getEstimatedValue(rowA.original);
            const b = getEstimatedValue(rowB.original);
            return a - b;
        },
    },
    {
        accessorKey: 'status',
        header: ({ column }) => {
            return (
                <Button variant="ghost" onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}>
                    {t('leads.tables.status', 'Status')}
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
            );
        },
        cell: ({ row }) => {
            const lead = row.original;

            // Once converted, the project supersedes the lead's own status.
            if (lead.project) {
                return (
                    // Matches the editable selector's shell so both variants of this
                    // column line up.
                    <Link href={`/projects/${lead.project.id}`} className="hover:bg-accent flex items-center rounded p-1 transition-colors">
                        <Badge variant="secondary" className="bg-success-subtle text-success-subtle-foreground">
                            {lead.project.contract_number || lead.project.contract_name}
                        </Badge>
                    </Link>
                );
            }

            return (
                <UnifiedStatusSelector value={lead.status} statusType="lead" mode="editable" updateRoute="leads.update-status" resourceId={lead.id} />
            );
        },
        filterFn: (row, columnId, filterValue) => {
            const status = row.getValue(columnId) as string;
            return matchesFilter(status, filterValue);
        },
    },
    {
        accessorKey: 'priority',
        header: ({ column }) => {
            return (
                <Button variant="ghost" onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}>
                    {t('leads.tables.priority', 'Priority')}
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
            );
        },
        cell: ({ row }) => {
            return (
                <UnifiedPrioritySelector
                    value={row.original.priority}
                    priorityType="lead"
                    mode="editable"
                    updateRoute="leads.update-priority"
                    resourceId={row.original.id}
                />
            );
        },
        filterFn: (row, columnId, filterValue) => {
            const priority = row.getValue(columnId) as string;
            return matchesFilter(priority, filterValue);
        },
        sortingFn: (rowA, rowB) => {
            const priorityOrder = { low: 1, medium: 2, high: 3 };
            const a = priorityOrder[rowA.original.priority as keyof typeof priorityOrder] || 2;
            const b = priorityOrder[rowB.original.priority as keyof typeof priorityOrder] || 2;
            return a - b;
        },
    },
    {
        accessorKey: 'company',
        header: ({ column }) => {
            return (
                <Button variant="ghost" onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}>
                    {t('leads.tables.company', 'Company')}
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
            );
        },
        cell: ({ row }) => {
            const company = row.original.company;

            if (!company) {
                return (
                    <div className="text-muted-foreground flex items-center gap-2">
                        <Building2 className="size-4" />
                        <span>{t('leads.tables.no_company', 'No company')}</span>
                    </div>
                );
            }

            return (
                <div className="text-left">
                    <Link href={`/companies/${company.id}`}>
                        <div className="flex items-center gap-2">
                            <Building2 className="text-muted-foreground size-4" />
                            <div className="hover:underline hover:underline-offset-4">{company.name}</div>
                        </div>
                    </Link>
                </div>
            );
        },
        filterFn: (row, columnId, filterValue) => {
            const company = row.original.company;
            const companyName = company?.name || '';
            return matchesFilter(companyName, filterValue);
        },
        sortingFn: (rowA, rowB) => {
            const a = rowA.original.company?.name || '';
            const b = rowB.original.company?.name || '';
            return a.localeCompare(b);
        },
    },
    // {
    //     accessorKey: "lead_source",
    //     header: "Source",
    //     cell: ({ row }) => {
    //         const source = row.original.lead_source;
    //         if (!source) return <span className="text-muted-foreground">-</span>;

    //         return (
    //             <Badge variant="outline">
    //                 {source.replace('_', ' ').toUpperCase()}
    //             </Badge>
    //         );
    //     },
    // },
    {
        accessorKey: 'lead_owner',
        header: ({ column }) => {
            return (
                <Button variant="ghost" onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}>
                    {t('leads.tables.lead_owner', 'Lead Owner')}
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
            );
        },
        cell: ({ row }) => {
            // Try lead_owner first, then assigned_user, then user for backward compatibility
            const owner = row.original.lead_owner || row.original.assigned_user || row.original.user;

            if (!owner) {
                return (
                    <div className="text-muted-foreground flex items-center gap-2">
                        <User className="size-4" />
                        <span>{t('leads.tables.unassigned', 'Unassigned')}</span>
                    </div>
                );
            }

            return (
                <div className="flex gap-2 text-left">
                    <Link href={`/employees/${owner.id}`}>
                        <div className="flex items-center gap-2">
                            <Avatar className="size-7">
                                <AvatarImage src={owner.avatar || ''} className="rounded-2xl" />
                                <AvatarFallback className="rounded-2xl">
                                    {(owner.first_name || '').charAt(0)}
                                    {(owner.last_name || '').charAt(0)}
                                </AvatarFallback>
                            </Avatar>
                            <div className="hover:underline hover:underline-offset-4">
                                {owner.first_name || ''} {owner.last_name || ''}
                            </div>
                        </div>
                    </Link>
                </div>
            );
        },
        filterFn: (row, columnId, filterValue) => {
            const owner = row.original.lead_owner || row.original.assigned_user || row.original.user;
            const ownerName = owner ? `${owner.first_name || ''} ${owner.last_name || ''}`.trim() : t('leads.tables.unassigned', 'Unassigned');
            return matchesFilter(ownerName, filterValue);
        },
        sortingFn: (rowA, rowB) => {
            const ownerA = rowA.original.lead_owner || rowA.original.assigned_user || rowA.original.user;
            const ownerB = rowB.original.lead_owner || rowB.original.assigned_user || rowB.original.user;
            const nameA = ownerA ? `${ownerA.first_name || ''} ${ownerA.last_name || ''}`.trim() : '';
            const nameB = ownerB ? `${ownerB.first_name || ''} ${ownerB.last_name || ''}`.trim() : '';
            return nameA.localeCompare(nameB);
        },
    },
];

// Export default columns for backward compatibility
export const columns = getColumns();
